import React from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ExitHookReadiness, PineExitSite } from "../../types/reportStrategyPine";
import { exitHookReadinessPineBadgeItem } from "../../views/Dashboard/Strategies/strategyTablePineBadges";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
  STRATEGY_PINE_DETAIL_TABLE_CLASS,
  STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS,
} from "./StrategyPineSectionCard";
import { STRATEGY_PINE_ALERT_CLASS, StrategyPineAlertIcon } from "./strategyPineAlertUi";

export type StrategyPineExitHooksProps = {
  exitHookReadiness?: ExitHookReadiness | string | null;
  exitSites: PineExitSite[];
  subtleText?: string;
  textColor?: string;
  borderColor?: string;
};

function exitStopLimitDisplay(site: PineExitSite, kind: "stop" | "limit"): string {
  if (kind === "stop") {
    return (
      site.stop_var ??
      site.materialized_stop_var ??
      (site.stop_expr ? String(site.stop_expr).slice(0, 48) : "—")
    );
  }
  return (
    site.limit_var ??
    site.materialized_limit_var ??
    (site.limit_expr ? String(site.limit_expr).slice(0, 48) : "—")
  );
}

export function StrategyPineExitHooks({
  exitHookReadiness,
  exitSites,
}: StrategyPineExitHooksProps): React.ReactElement | null {
  if (!exitHookReadiness && exitSites.length === 0) return null;

  const readinessBadge = exitHookReadiness
    ? exitHookReadinessPineBadgeItem(exitHookReadiness)
    : null;

  return (
    <Field data-strategy-pine-exit-hooks="" className="min-w-0 gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Exit hooks</FieldLabel>
        {readinessBadge ? (
          <Badge variant={readinessBadge.variant}>{readinessBadge.label}</Badge>
        ) : null}
      </div>

      {exitHookReadiness === "unsupported" ? (
        <Alert variant="warning" className={STRATEGY_PINE_ALERT_CLASS}>
          <StrategyPineAlertIcon tone="warning" />
          <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            No hookable stop/limit exits were detected. Compose may only add labels for management
            on this script.
          </AlertDescription>
        </Alert>
      ) : null}

      {exitSites.length > 0 ? (
        <div
          data-strategy-pine-detail-table=""
          data-shadcn-data-table=""
          className={STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS}
        >
          <Table className={STRATEGY_PINE_DETAIL_TABLE_CLASS}>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="min-w-[120px]">Stop</TableHead>
                <TableHead className="min-w-[120px]">Limit</TableHead>
                <TableHead className="min-w-[56px]">Line</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exitSites.map((site, idx) => (
                <TableRow key={`${site.line_start ?? idx}-${idx}`}>
                  <TableCell className="font-mono text-xs whitespace-normal">
                    {exitStopLimitDisplay(site, "stop")}
                  </TableCell>
                  <TableCell className="font-mono text-xs whitespace-normal">
                    {exitStopLimitDisplay(site, "limit")}
                  </TableCell>
                  <TableCell className="text-xs">{site.line_start ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
          No strategy.exit sites detected.
        </FieldDescription>
      )}
    </Field>
  );
}
